import * as React from 'react';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { __, __r } from 'react-i18n/lib';
import { Link } from 'react-router';
import { Button } from '../General/Button';
import { ErrorAlert } from '../Alerts/ErrorAlert';
import { Input } from '../General/Input';

interface Props {
  cartId: string | undefined;
  pathname: string;
  search: string;
  baseApiUrl: string | undefined;
  baseUrl: string | undefined;
  error: string;
}

function LoginForm({
  cartId,
  pathname,
  search,
  baseApiUrl,
  baseUrl,
  error,
}: Props) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');

  return (
    <Wrapper>
      <Header>{`${__('Máte svoj účet')}?`}</Header>
      <div>{error && <ErrorAlert message={error} />}</div>
      <Form
        action={`${baseApiUrl}/oauth/authorize?${
          cartId && cartId !== 'null' ? `cart_id=${cartId}&` : ''
        }client_id=eshop&scope=all&redirect_uri=${baseUrl}/${__(
          'prihlasenie',
        )}?cart_id=${cartId}&state=${`${baseUrl}${pathname}${
          search && search !== '' ? `%3F${search}` : ''
        }`}`}
        method="POST"
        autoComplete="true"
      >
        <FormInput
          type="email"
          placeholder={__('Prihlasovací e-mail')}
          value={email}
          name="username"
          // tslint:disable-next-line:jsx-no-lambda
          onChange={e => setEmail(e.target.value)}
          tabIndex={1}
        />
        <FormInput
          type="password"
          placeholder={__('Heslo')}
          value={password}
          name="password"
          // tslint:disable-next-line:jsx-no-lambda
          onChange={e => setPassword(e.target.value)}
          tabIndex={2}
        />
        <PasswordLink to={__r('routes:zabudnute-heslo', '/zabudnute-heslo')}>
          <ForgotPassword>{__('Zabudli ste heslo')}?</ForgotPassword>
        </PasswordLink>
        <LoginButton
          disabled={password.length === 0 || email.length === 0}
          type="submit"
        >
          {__('Prihlásiť sa')}
        </LoginButton>
      </Form>
      <NoAccountWrapper>
        {`${__('Ešte nemáte účet')}? `}
        <Link to={__r('routes:registracia', '/registracia')}>
          <Register>{__('Zaregistrujte sa')}</Register>
        </Link>
      </NoAccountWrapper>
    </Wrapper>
  );
}

const secondaryText = css`
  text-decoration-color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.font.primary};
  font-weight: 300;
  font-size: ${rem(14)};
  line-height: ${rem(21)};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: ${rem(320)};
  background: ${({ theme }) => theme.colors.white};
  text-align: center;
`;

const Header = styled.h1`
  font-size: ${rem(30)};
  text-align: center;
  font-weight: 500;
  position: relative;
  font-family: ${({ theme }) => theme.font.secondary};
  margin-bottom: ${rem(32)};
  line-height: ${rem(39)};
  color: ${({ theme }) => theme.colors.textPrimary};
  text-transform: uppercase;

  ${({ theme }) => theme.mediab.l925`
    font-size: ${rem(24)};
    line-height: ${rem(28)};
  `}
`;

const Form = styled.form`
  margin: 0;
  & input:first-of-type {
    margin-bottom: ${rem(16)};
  }
`;

const LoginButton = styled(Button)`
  width: 100%;
  max-width: ${rem(320)};
  margin: ${rem(24)} 0;

  &:disabled {
    background-color: #dcd6d6;
    color: ${({ theme }) => theme.colors.black};
  }

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.primaryOnHover};
    outline: none;
  }
`;

const PasswordLink = styled(Link)`
  text-decoration-color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 500;
`;

const ForgotPassword = styled.p`
  ${secondaryText};
  text-align: right;
  margin: ${rem(16)} 0 0;
  width: 100%;
  font-weight: 500;
`;

const Register = styled.p`
  ${secondaryText};
  display: inline-block;
  text-decoration: underline;
  margin: 0;
  margin-left: ${rem(4)};
  font-weight: 500;
`;

const NoAccountWrapper = styled.span`
  ${secondaryText};
  display: flex;
  justify-content: center;
`;

export const FormInput = styled(Input)`
  font-size: ${rem(14)};
  line-height: ${rem(18)};
`;

export { LoginForm };
